import React, { useState, useEffect, useRef } from 'react';
import {
  LogOut,
  User,
  Calendar,
  Zap,
  BarChart3,
  Users,
  RefreshCw,
  Download,
  FileText,
  Mail,
  Hash,
  MapPin,
  Building2,
  Sun,
  ArrowLeft,
  Leaf,
  Home
} from 'lucide-react';
import { format } from 'date-fns';
import { fr, nl, enUS } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAutoLogout } from '../hooks/useAutoLogout';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import MemberEanChart from './MemberEanChart';
import MemberSidebar from './MemberSidebar';
import MemberTutorial, { type TutorialMode } from './MemberTutorial';
import { ContractTab } from './ContractTab';
import { HelpCircle } from 'lucide-react';
import { resolvePriceForMonth, type TariffOverride } from '../utils/tariffOverrides';

// --- Palette et styles partagés du dashboard membre ---
// Mandarine = ce qui est partagé dans la communauté, bleu nuit = ce qui passe par le réseau.
const DASH = {
  shared: '#F59B42',
  sharedLight: '#F59B42',
  networkLight: '#2F6F8F',
  network: '#2F6F8F',
  excise: '#A7B3BC',
  ink: '#0C1C2C',
  teal: '#00516F',
  muted: '#51626E',
  border: '#C9DFE5',
  grid: '#E3ECEF',
  soft: '#F2F2F3',
  accent: '#FCAE0A',
  sharedText: '#B35F0F',
};

const DASH_BTN = 'inline-flex items-center justify-center gap-2 h-10 px-4 rounded-[10px] border border-[#C9DFE5] bg-white text-sm font-medium text-[#0C1C2C] hover:border-[#00516F] disabled:opacity-50 transition-colors';
const DASH_SELECT = 'h-9 px-3 border border-[#C9DFE5] rounded-[10px] text-sm font-semibold text-[#0C1C2C] bg-white focus:outline-none focus:ring-2 focus:ring-[#00516F]/30';
const DASH_CARD = 'bg-white border border-[#C9DFE5] rounded-2xl p-5 md:p-6';
const DASH_H2 = 'font-sans text-xl leading-tight font-bold text-[#00516F]';
const dashTabClass = (on: boolean) =>
  `bg-transparent px-0.5 py-3 mr-7 -mb-px font-sans text-base font-semibold border-b-[3px] transition-colors ${
    on ? 'text-[#00516F] border-[#FCAE0A]' : 'text-[#51626E] border-transparent hover:text-[#00516F]'
  }`;
const DASH_SEG = (on: boolean) =>
  `px-3 h-8 text-sm font-medium transition-colors ${on ? 'bg-[#00516F] text-white' : 'bg-white text-[#51626E] hover:bg-[#F2F2F3]'}`;

function BilanBlock({
  title, shared, network, sharedLabel, networkLabel, percentLabel, percent,
}: {
  title: string; shared: number; network: number; sharedLabel: string; networkLabel: string;
  percentLabel?: string; percent?: number;
}) {
  const total = shared + network;
  return (
    <div className="flex flex-col gap-2.5">
      <span className="font-sans text-sm font-bold uppercase tracking-wide text-[#0C1C2C]">{title}</span>
      <div
        className="flex gap-[3px] h-3.5"
        role="img"
        aria-label={`${title} : ${(shared / 1000).toFixed(2)} MWh / ${(network / 1000).toFixed(2)} MWh`}
      >
        {total > 0 ? (
          <>
            {shared > 0 && <div className="rounded-l-md" style={{ flex: `${shared} 1 0`, background: DASH.sharedLight, border: `1.5px solid ${DASH.shared}`, borderTopRightRadius: network > 0 ? 0 : 6, borderBottomRightRadius: network > 0 ? 0 : 6 }} />}
            {network > 0 && <div className="rounded-r-md" style={{ flex: `${network} 1 0`, background: DASH.networkLight, border: `1.5px solid ${DASH.network}`, borderTopLeftRadius: shared > 0 ? 0 : 6, borderBottomLeftRadius: shared > 0 ? 0 : 6 }} />}
          </>
        ) : (
          <div className="flex-1 rounded-md bg-[#E3ECEF]" />
        )}
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        <BilanValue color={DASH.sharedLight} stroke={DASH.shared} label={sharedLabel} value={`${(shared / 1000).toFixed(2)} MWh`} />
        <BilanValue color={DASH.networkLight} stroke={DASH.network} label={networkLabel} value={`${(network / 1000).toFixed(2)} MWh`} />
        {percentLabel !== undefined && percent !== undefined && (
          <BilanValue label={percentLabel} value={`${percent.toFixed(1)}%`} />
        )}
      </div>
    </div>
  );
}

function BilanValue({ color, stroke, label, value }: { color?: string; stroke?: string; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      {color ? <span className="w-3 h-3 rounded-[3px] mt-1 flex-shrink-0" style={{ background: color, border: stroke ? `1.5px solid ${stroke}` : undefined }} /> : <span className="w-3 h-3 mt-1 flex-shrink-0" />}
      <div>
        <div className="text-[13px] text-[#51626E]">{label}</div>
        <div className="font-sans text-xl font-bold tabular-nums leading-tight">{value}</div>
      </div>
    </div>
  );
}

function BilanSide({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3.5">
      <span className="w-11 h-11 rounded-xl bg-[#F2F2F3] flex items-center justify-center text-[#00516F] flex-shrink-0">{icon}</span>
      <div>
        <div className="text-[13px] text-[#51626E]">{label}</div>
        <div className="font-sans text-2xl font-bold tabular-nums leading-tight">{value}</div>
      </div>
    </div>
  );
}

// Arrondit vers le haut à une valeur « ronde » (1, 2, 2.5, 5 × 10^n) pour des graduations lisibles
/** Graduations régulières (pas de 1, 2 ou 5 × 10^n) couvrant 0 → max, environ 4 intervalles. */
function niceTicks(max: number): number[] {
  if (!(max > 0)) return [0];
  const raw = max / 4;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = ([1, 2, 5, 10].find(m => m * p >= raw) || 10) * p;
  const top = Math.ceil(max / step) * step;
  return Array.from({ length: Math.round(top / step) + 1 }, (_, i) => Math.round(i * step * 1000) / 1000);
}


function EnergyTooltip({ active, payload, label }: any) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s: number, e: any) => s + (Number(e.value) || 0), 0);
  return (
    <div className="bg-white border border-[#C9DFE5] rounded-xl shadow-lg px-4 py-3 text-sm min-w-[220px] font-sans">
      <p className="font-semibold text-[#0C1C2C] mb-2 pb-2 border-b border-[#E3ECEF]">{label}</p>
      {[...payload].reverse().map((e: any) => (
        <div key={e.dataKey} className="flex justify-between items-center gap-6 py-0.5">
          <span className="flex items-center gap-2 text-[#51626E]">
            <span className="w-3 h-3 rounded-[3px]" style={{ background: e.fill || e.color }} />
            {e.name}
          </span>
          <span className="font-semibold tabular-nums text-[#0C1C2C]">{Number(e.value || 0).toLocaleString()} kWh</span>
        </div>
      ))}
      {payload.length > 1 && (
        <div className="flex justify-between items-center gap-6 pt-2 mt-1.5 border-t border-[#E3ECEF] font-semibold text-[#0C1C2C]">
          <span>{t('memberUi.total')}</span>
          <span className="tabular-nums">{total.toLocaleString()} kWh</span>
        </div>
      )}
    </div>
  );
}

function TypeChip({ type, label }: { type: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 h-[22px] px-[7px] rounded-full border border-[#C9DFE5] text-[11.5px] font-medium text-[#00516F] whitespace-nowrap">
      {type === 'consumer' ? <Zap className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
      {label}
    </span>
  );
}


// Aperçu décoratif du graphique quart-horaire (aires claires empilées)
const QH_PREVIEW = (() => {
  const W = 1040, H = 120, N = 168;
  const tot: string[] = [];
  const sh: string[] = [];
  for (let i = 0; i <= N; i++) {
    const x = (i / N) * W;
    const h = i % 24;
    const day = Math.max(0, Math.sin(((h - 6) / 12) * Math.PI));
    const base = 0.32 + 0.12 * Math.sin(i / 5) + 0.45 * day * (0.8 + 0.2 * Math.sin(i / 24 * 2.3));
    const shared = Math.min(base * 0.92, 0.12 + 0.5 * day * (0.75 + 0.25 * Math.cos(i / 24 * 1.7)));
    tot.push(`${x.toFixed(1)},${(H - base * (H - 12)).toFixed(1)}`);
    sh.push(`${x.toFixed(1)},${(H - shared * (H - 12)).toFixed(1)}`);
  }
  return {
    total: `M0,${H} L${tot.join(' L')} L${W},${H} Z`,
    totalLine: `M${tot.join(' L')}`,
    shared: `M0,${H} L${sh.join(' L')} L${W},${H} Z`,
    sharedLine: `M${sh.join(' L')}`,
  };
})();

interface MemberDashboardProps {
  user: any;
  onLogout: () => void;
  isAdminView?: boolean;
  availableServices?: string[];
  onSwitchService?: () => void;
  canViewQh?: boolean;
}

export function MemberDashboard({ user, onLogout, isAdminView = false, availableServices = [], onSwitchService, canViewQh = true }: MemberDashboardProps) {
  const { t, i18n } = useTranslation();
  const [participant, setParticipant] = useState<any>(null);
  const [groupParticipants, setGroupParticipants] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [availableYears, setAvailableYears] = useState<string[]>([new Date().getFullYear().toString()]);
  const [selectedMonth, setSelectedMonth] = useState<string>('total');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [availableDataPeriod, setAvailableDataPeriod] = useState<{ start: string; end: string } | null>(null);
  const yearsInitialized = useRef(false);
  const [visibleSeries, setVisibleSeries] = useState<Set<string>>(new Set(['volume_partage', 'volume_complementaire', 'injection_partagee', 'injection_complementaire']));
  const [activeTab, setActiveTab] = useState<'energie' | 'contrat'>('energie');
  const [energySelectedParticipantId, setEnergySelectedParticipantId] = useState<string>('all');
  const [energyDetailStart, setEnergyDetailStart] = useState<string>('');
  const [energyDetailEnd, setEnergyDetailEnd] = useState<string>('');
  const [energyUnit, setEnergyUnit] = useState<'kWh' | 'MWh'>('MWh');
  const [detailViewMode, setDetailViewMode] = useState<'energetique' | 'economique'>('energetique');
  const [detailBillingConfig, setDetailBillingConfig] = useState<any>(null);
  const [detailIdxValues, setDetailIdxValues] = useState<Record<string, Record<string, number>>>({});
  const [detailTariffOverrides, setDetailTariffOverrides] = useState<Record<string, any[]>>({});
  const [showQhChart, setShowQhChart] = useState(false);
  const [showQhPeriodModal, setShowQhPeriodModal] = useState(false);
  const [qhPeriodStart, setQhPeriodStart] = useState<string>('');
  const [qhPeriodEnd, setQhPeriodEnd] = useState<string>('');
  const [qhModalStart, setQhModalStart] = useState<string>('');
  const [qhModalEnd, setQhModalEnd] = useState<string>('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialMode, setTutorialMode] = useState<TutorialMode>('full');
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'nl': return nl;
      case 'en': return enUS;
      default: return fr;
    }
  };

  // Hook de déconnexion automatique
  useAutoLogout({
    onLogout,
    timeoutMinutes: 15,
    isLoggedIn: true
  });

  useEffect(() => {
    yearsInitialized.current = false;
    loadParticipantData();
  }, [user]);

  useEffect(() => {
    if (participant && !isAdminView) {
      if ((participant.account_status || 'pending') !== 'approved') return;
      // Première connexion : guide complet. Membre existant : démo de l'onglet Contrat, une seule fois.
      const mode: TutorialMode | null = participant.tutorial_seen === false
        ? 'full'
        : participant.contract_tutorial_seen === false ? 'contract' : null;
      if (mode) {
        const timer = setTimeout(() => {
          setTutorialMode(mode);
          setShowTutorial(true);
        }, 800);
        return () => clearTimeout(timer);
      }
    }
  }, [participant, isAdminView, user]);

  const handleCloseTutorial = () => {
    setShowTutorial(false);
    if (participant?.id && !isAdminView) {
      const full = tutorialMode === 'full';
      // Enregistré côté serveur (fonction member_mark_tutorial_seen) ; à défaut, mise à jour directe.
      supabase.rpc('member_mark_tutorial_seen', { p_participant_id: participant.id, p_full: full })
        .then(({ error }: { error: { message: string } | null }) => {
          if (!error) return;
          supabase.from('participants')
            .update(full ? { tutorial_seen: true, contract_tutorial_seen: true } : { contract_tutorial_seen: true })
            .eq('id', participant.id)
            .then(({ error: e2 }: { error: { message: string } | null }) => { if (e2) console.error('Guide non enregistré :', e2.message); });
        });
      setParticipant((prev: any) => prev ? { ...prev, contract_tutorial_seen: true, ...(full ? { tutorial_seen: true } : {}) } : prev);
    }
  };

  useEffect(() => {
    if (participant) {
      loadGroupData();
    }
  }, [participant, selectedYear]);

  // Load billing config, indexation values, and tariff overrides for economic detail view
  useEffect(() => {
    supabase.from('billing_config').select('key, value').then(({ data }) => {
      if (data && data.length > 0) {
        const cfg: Record<string, number> = {};
        for (const row of data) cfg[row.key] = Number(row.value);
        setDetailBillingConfig(cfg);
      }
    });
    supabase.from('indexation_parameters').select('param_name, month_key, value').then(({ data }) => {
      if (data && data.length > 0) {
        const map: Record<string, Record<string, number>> = {};
        for (const row of data) {
          if (!map[row.param_name]) map[row.param_name] = {};
          map[row.param_name][row.month_key] = Number(row.value);
        }
        setDetailIdxValues(map);
      }
    });
  }, []);

  useEffect(() => {
    const ids = groupParticipants.map(p => p.id).filter(Boolean);
    if (ids.length === 0) return;
    import('../utils/tariffOverrides').then(({ fetchTariffOverridesBatch }) => {
      fetchTariffOverridesBatch(ids).then(setDetailTariffOverrides).catch(() => {});
    });
  }, [groupParticipants.map(p => p.id).join(',')]);

  const loadParticipantData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Chargement du participant pour user:', user);

      let participantId: string | undefined = user.participant_id;

      // If no participant_id in user metadata, try the edge function lookup
      // which checks participant_accounts (supports multiple accounts per EAN)
      if (!participantId && user.email) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;

        if (accessToken) {
          try {
            const response = await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ action: 'get_user_participant' }),
            });
            const result = await response.json();
            if (result.found && result.participant_id) {
              participantId = result.participant_id;
            }
          } catch (err) {
            console.warn('Edge function lookup failed, falling back to email search:', err);
          }
        }
      }

      let participantData: any[] | null = null;
      let error: any = null;

      if (participantId) {
        console.log('🔍 Chargement par participant_id:', participantId);
        const result = await supabase
          .from('participants')
          .select('*')
          .eq('id', participantId);
        participantData = result.data;
        error = result.error;
      } else if (user.email) {
        console.log('🔍 Chargement par email:', user.email);
        const result = await supabase
          .from('participants')
          .select('*')
          .eq('email', user.email);
        participantData = result.data;
        error = result.error;
      } else {
        toast.error(t('memberUi.errNoId'));
        setLoading(false);
        return;
      }

      if (error) {
        console.error('Erreur chargement participant:', error);
        toast.error(t('memberUi.errLoadParticipant'));
        return;
      }

      if (!participantData || participantData.length === 0) {
        toast.error(t('memberUi.errNoParticipant'));
        return;
      }

      if (participantData.length > 1) {
        console.warn('⚠️ Plusieurs participants trouvés:', participantData);
        setParticipant(participantData[0]);
        return;
      }

      console.log('✅ Participant chargé:', participantData[0]);
      setParticipant(participantData[0]);
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error(t('memberUi.errLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const loadGroupData = async () => {
    try {
      console.log('🔍 Chargement des données du groupe...');
      console.log('👤 Participant actuel:', participant);

      if (!participant.groupe) {
        console.log('ℹ️ Participant sans groupe, affichage individuel');
        // Pas de groupe, charger seulement les données individuelles
        loadIndividualData();
        return;
      }

      console.log(`👥 Chargement du groupe: "${participant.groupe}"`);

      // Charger TOUS les participants du même groupe
      const { data: allGroupParticipants, error: groupError } = await supabase
        .from('participants')
        .select('*')
        .eq('groupe', participant.groupe);

      if (groupError) {
        console.error('❌ Erreur chargement groupe:', groupError);
        toast.error(t('memberUi.errLoadGroup'));
        return;
      }

      console.log(`✅ ${allGroupParticipants?.length || 0} participants trouvés dans le groupe "${participant.groupe}"`);
      console.log('📋 Participants du groupe:', allGroupParticipants?.map(p => ({ name: p.name, ean: p.ean_code, hasData: !!p.monthly_data })));

      if (!allGroupParticipants || allGroupParticipants.length === 0) {
        console.log('⚠️ Aucun participant trouvé dans le groupe');
        loadIndividualData();
        return;
      }

      // Calculer la période de disponibilité des données (seulement au premier chargement)
      calculateAvailableDataPeriod(allGroupParticipants, !yearsInitialized.current);
      yearsInitialized.current = true;

      // Traiter les données mensuelles de TOUS les participants du groupe
      const processedData = processGroupMonthlyData(allGroupParticipants, selectedYear);

      setGroupParticipants(allGroupParticipants);
      setMonthlyData(processedData.monthlyData);

      console.log('📊 Données finales du groupe:', {
        participants: allGroupParticipants.length,
        monthsWithData: processedData.monthlyData.length,
        totalVolumePartage: processedData.monthlyData.reduce((sum, m) => sum + m.volume_partage, 0),
        totalVolumeComplementaire: processedData.monthlyData.reduce((sum, m) => sum + m.volume_complementaire, 0)
      });

    } catch (error) {
      console.error('❌ Erreur chargement groupe:', error);
      toast.error(t('memberUi.errLoadGroupData'));
    }
  };

  const calculateAvailableDataPeriod = (participants: any[], isInitialLoad = true) => {
    let allMonths: string[] = [];

    participants.forEach(p => {
      if (!p.monthly_data) return;

      let monthlyData;
      try {
        if (typeof p.monthly_data === 'string') {
          monthlyData = JSON.parse(p.monthly_data);
        } else {
          monthlyData = p.monthly_data;
        }
        allMonths.push(...Object.keys(monthlyData));
      } catch (error) {
        console.warn('Erreur parsing monthly_data:', error);
      }
    });

    if (allMonths.length === 0) {
      setAvailableDataPeriod(null);
      return;
    }

    allMonths.sort();
    const startMonth = allMonths[0];
    const endMonth = allMonths[allMonths.length - 1];

    setAvailableDataPeriod({ start: startMonth, end: endMonth });

    const startYear = parseInt(startMonth.split('-')[0]);
    const endYear = parseInt(endMonth.split('-')[0]);
    const years: string[] = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y.toString());
    }
    setAvailableYears(years);
    if (isInitialLoad) {
      setSelectedYear(endYear.toString());
    }
  };

  const loadIndividualData = () => {
    console.log('👤 Chargement des données individuelles pour:', participant.name);

    calculateAvailableDataPeriod([participant], !yearsInitialized.current);
    yearsInitialized.current = true;

    if (!participant.monthly_data) {
      console.log('⚠️ Aucune donnée mensuelle pour ce participant');
      setMonthlyData([]);
      setGroupParticipants([participant]);
      return;
    }

    const processedData = processIndividualMonthlyData(participant, selectedYear);
    setMonthlyData(processedData);
    setGroupParticipants([participant]);
  };

  const processGroupMonthlyData = (participants: any[], year: string) => {
    console.log(`📊 Traitement des données du groupe pour l'année ${year}`);
    
    const monthlyTotals: { [month: string]: {
      volume_partage: number;
      volume_complementaire: number;
      injection_partagee: number;
      injection_complementaire: number;
      participantCount: number;
    } } = {};

    participants.forEach(participant => {
      console.log(`🔍 Traitement participant: ${participant.name} (${participant.ean_code})`);
      
      if (!participant.monthly_data) {
        console.log(`⚠️ ${participant.name} n'a pas de monthly_data`);
        return;
      }

      let monthlyData;
      try {
        if (typeof participant.monthly_data === 'string') {
          monthlyData = JSON.parse(participant.monthly_data);
        } else {
          monthlyData = participant.monthly_data;
        }
      } catch (error) {
        console.warn(`⚠️ Erreur parsing monthly_data pour ${participant.name}:`, error);
        return;
      }

      console.log(`📅 Mois disponibles pour ${participant.name}:`, Object.keys(monthlyData));

      Object.entries(monthlyData).forEach(([month, data]: [string, any]) => {
        if (!month.startsWith(year)) {
          return;
        }

        console.log(`📊 ${participant.name} - ${month}:`, data);

        if (!monthlyTotals[month]) {
          monthlyTotals[month] = {
            volume_partage: 0,
            volume_complementaire: 0,
            injection_partagee: 0,
            injection_complementaire: 0,
            participantCount: 0
          };
        }

        monthlyTotals[month].volume_partage += Number(data.volume_partage || 0);
        monthlyTotals[month].volume_complementaire += Number(data.volume_complementaire || 0);
        monthlyTotals[month].injection_partagee += Number(data.injection_partagee || 0);
        monthlyTotals[month].injection_complementaire += Number(data.injection_complementaire || 0);
        monthlyTotals[month].participantCount += 1;
      });
    });

    console.log('📊 Totaux mensuels calculés:', monthlyTotals);

    const monthlyDataArray = Object.entries(monthlyTotals).map(([month, totals]) => ({
      month: format(new Date(month + '-01'), 'MMM yyyy', { locale: getDateLocale() }),
      monthKey: month,
      volume_partage: Math.round(totals.volume_partage),
      volume_complementaire: Math.round(totals.volume_complementaire),
      injection_partagee: Math.round(totals.injection_partagee),
      injection_complementaire: Math.round(totals.injection_complementaire),
      participantCount: totals.participantCount
    })).sort((a, b) => a.monthKey.localeCompare(b.monthKey));

    console.log('📈 Données finales pour le graphique:', monthlyDataArray);

    return { monthlyData: monthlyDataArray };
  };

  const processIndividualMonthlyDataAllYears = (p: any) => {
    if (!p.monthly_data) return [];
    let monthlyData: any;
    try {
      monthlyData = typeof p.monthly_data === 'string' ? JSON.parse(p.monthly_data) : p.monthly_data;
    } catch {
      return [];
    }
    return Object.entries(monthlyData)
      .map(([month, data]: [string, any]) => ({
        monthKey: month,
        volume_partage: Number(data.volume_partage || 0),
        volume_complementaire: Number(data.volume_complementaire || 0),
        injection_partagee: Number(data.injection_partagee || 0),
        injection_complementaire: Number(data.injection_complementaire || 0),
      }))
      .filter(d => d.volume_partage + d.volume_complementaire + d.injection_partagee + d.injection_complementaire > 0)
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  };

  const processIndividualMonthlyData = (participant: any, year: string) => {
    console.log(`👤 Traitement des données individuelles pour ${participant.name}`);
    
    if (!participant.monthly_data) {
      return [];
    }

    let monthlyData;
    try {
      if (typeof participant.monthly_data === 'string') {
        monthlyData = JSON.parse(participant.monthly_data);
      } else {
        monthlyData = participant.monthly_data;
      }
    } catch (error) {
      console.warn('⚠️ Erreur parsing monthly_data:', error);
      return [];
    }

    const monthlyDataArray = Object.entries(monthlyData)
      .filter(([month]) => month.startsWith(year))
      .map(([month, data]: [string, any]) => ({
        month: format(new Date(month + '-01'), 'MMM yyyy', { locale: getDateLocale() }),
        monthKey: month,
        volume_partage: Number(data.volume_partage || 0),
        volume_complementaire: Number(data.volume_complementaire || 0),
        injection_partagee: Number(data.injection_partagee || 0),
        injection_complementaire: Number(data.injection_complementaire || 0),
        participantCount: 1
      }))
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

    return monthlyDataArray;
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadParticipantData();
      if (participant?.groupe) {
        await loadGroupData();
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    try {
      await supabase.auth.signOut({ scope: 'global' });
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('supabase.auth.') || key.startsWith('sb-')) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();
      onLogout();
    } catch (error) {
      console.error('Error logging out:', error);
      onLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Calculer les totaux annuels
  const yearlyTotals = monthlyData.reduce((totals, month) => {
    console.log(`📊 Ajout mois ${month.month}:`, {
      volume_partage: month.volume_partage,
      volume_complementaire: month.volume_complementaire,
      injection_partagee: month.injection_partagee,
      injection_complementaire: month.injection_complementaire
    });
    
    return {
      volume_partage: totals.volume_partage + (month.volume_partage || 0),
      volume_complementaire: totals.volume_complementaire + (month.volume_complementaire || 0),
      injection_partagee: totals.injection_partagee + (month.injection_partagee || 0),
      injection_complementaire: totals.injection_complementaire + (month.injection_complementaire || 0)
    };
  }, {
    volume_partage: 0,
    volume_complementaire: 0,
    injection_partagee: 0,
    injection_complementaire: 0
  });

  console.log('📊 TOTAUX ANNUELS CALCULÉS:', yearlyTotals);

  const totalConsumption = yearlyTotals.volume_partage + yearlyTotals.volume_complementaire;
  const totalInjection = yearlyTotals.injection_partagee + yearlyTotals.injection_complementaire;
  const injectionSharedPercentage = totalInjection > 0 ? (yearlyTotals.injection_partagee / totalInjection) * 100 : 0;
  const consumptionSharedPercentage = totalConsumption > 0 ? (yearlyTotals.volume_partage / totalConsumption) * 100 : 0;

  // Calculer le CO2 évité (kg) : énergie partagée * 0.2 kg CO2/kWh
  const co2Avoided = yearlyTotals.volume_partage * 0.2;

  const isProsumer = participant?.type === 'prosumer';
  const hasConsumers = groupParticipants.some(p => p.type === 'consumer');
  const hasProducers = groupParticipants.some(p => p.type === 'producer');
  const hasProsumers = groupParticipants.some(p => p.type === 'prosumer');
  const isMixedGroup = (hasConsumers && hasProducers) || (hasConsumers && hasProsumers) || (hasProducers && hasProsumers);
  // Déterminer si le groupe est composé principalement de consommateurs
  const isConsumerGroup = !isMixedGroup && groupParticipants.filter(p => p.type === 'consumer').length > groupParticipants.filter(p => p.type === 'producer').length;

  const energyChartData = (() => {
    if (energySelectedParticipantId === 'all' || groupParticipants.length === 0) return monthlyData;
    const selectedP = groupParticipants.find(p => p.id === energySelectedParticipantId);
    if (!selectedP) return monthlyData;
    return processIndividualMonthlyData(selectedP, selectedYear);
  })();

  const energyDetailMonthsWithData = React.useMemo(() => {
    if (groupParticipants.length === 0) return [];
    const keys = new Set<string>();
    groupParticipants.forEach(p => {
      processIndividualMonthlyDataAllYears(p).forEach(d => keys.add(d.monthKey));
    });
    return Array.from(keys).sort();
  }, [groupParticipants]);

  React.useEffect(() => {
    if (energyDetailMonthsWithData.length > 0 && !energyDetailStart) {
      setEnergyDetailStart(energyDetailMonthsWithData[0]);
      setEnergyDetailEnd(energyDetailMonthsWithData[energyDetailMonthsWithData.length - 1]);
    }
  }, [energyDetailMonthsWithData.join(',')]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-neutral-600">{t('memberDashboard.loading')}</p>
        </div>
      </div>
    );
  }

  if (!participant) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-brand-flame">{t('memberDashboard.notFound')}</p>
          <button onClick={handleLogout} className="mt-4 text-brand-teal hover:text-brand-teal">
            {t('memberDashboard.backHome')}
          </button>
        </div>
      </div>
    );
  }

  const accountStatus = participant.account_status || 'pending';

  if (accountStatus !== 'approved' && !isAdminView) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="w-10 h-10 text-amber-600" />
          </div>

          {accountStatus === 'pending' ? (
            <>
              <h2 className="text-2xl font-bold text-neutral-900 mb-3">
                {t('memberUi.pendingTitle')}
              </h2>
              <p className="text-neutral-600 mb-6">
                {t('memberUi.pendingText')}
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-800 text-sm">
                  {t('memberUi.pendingDelay')}
                </p>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-neutral-900 mb-3">
                {t('memberUi.refusedTitle')}
              </h2>
              <p className="text-neutral-600 mb-6">
                {t('memberUi.refusedText')}
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800 text-sm">
                  {t('memberUi.contactLine')}
                </p>
              </div>
            </>
          )}

          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors disabled:opacity-50"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isLoggingOut ? t('memberUi.loggingOut') : t('memberUi.backHome')}</span>
          </button>
        </div>
      </div>
    );
  }

  const isGroupDashboard = participant.groupe && groupParticipants.length > 1;
  const dashboardTitle = isGroupDashboard ? `${t('memberDashboard.group')} ${participant.groupe}` : participant.name;

  // --- Valeurs d'affichage (forme uniquement) ---
  const showBothBlocks = isProsumer || isMixedGroup;
  const co2Label = co2Avoided >= 1000 ? `${(co2Avoided / 1000).toFixed(1)} t` : `${Math.round(co2Avoided)} kg`;
  const statusLabel = isGroupDashboard ? t('memberDashboard.stats.participants') : t('memberDashboard.stats.status');
  const statusValue = isGroupDashboard
    ? groupParticipants.length
    : showBothBlocks
    ? (isMixedGroup ? t('memberUi.mixed') : 'Prosumer')
    : t('memberDashboard.individual');

  const energyChartType = energySelectedParticipantId !== 'all'
    ? (groupParticipants.find(p => p.id === energySelectedParticipantId)?.type || participant.type)
    : participant.type;
  const isMultiChart = energyChartType === 'prosumer' || (energySelectedParticipantId === 'all' && isMixedGroup);

  const consoSeries = [
    { key: 'volume_partage', label: t('memberDashboard.chart.sharedConsumption'), color: DASH.shared },
    { key: 'volume_complementaire', label: t('memberDashboard.chart.networkConsumption'), color: DASH.network },
  ];
  const injSeries = [
    { key: 'injection_partagee', label: t('memberDashboard.chart.sharedInjection'), color: DASH.shared },
    { key: 'injection_complementaire', label: t('memberDashboard.chart.networkInjection'), color: DASH.network },
  ];

  // Bascule d'une série : on garde toujours au moins une série visible par graphique
  const toggleSeries = (key: string, pairKey: string) => {
    setVisibleSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.has(pairKey)) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Échelle propre à chaque graphique (Consommation / Injection), selon ses séries visibles
  const stackMax = (keys: string[]) => Math.max(0, ...energyChartData.map((d: any) =>
    keys.reduce((sum, k) => sum + (visibleSeries.has(k) ? Number(d[k]) || 0 : 0), 0)));
  const consoYMax = stackMax(['volume_partage', 'volume_complementaire']);
  const injYMax = stackMax(['injection_partagee', 'injection_complementaire']);

  const renderEnergyChart = (series: { key: string; label: string; color: string }[], heightClass: string, yMax?: number) => {
    const shown = series.filter(s => !isMultiChart || visibleSeries.has(s.key));
    return (
      <div className={`${heightClass} -mx-2 md:mx-0`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={energyChartData} margin={{ left: 0, right: 8, top: 8, bottom: 0 }} barCategoryGap="28%">
            <CartesianGrid stroke={DASH.grid} vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: DASH.muted }} axisLine={{ stroke: DASH.border }} tickLine={false} />
            <YAxis
              width={56}
              tick={{ fontSize: 12, fill: DASH.muted }}
              axisLine={false}
              tickLine={false}
              domain={yMax && yMax > 0 ? [0, niceTicks(yMax)[niceTicks(yMax).length - 1]] : [0, 'auto']}
              ticks={yMax && yMax > 0 ? niceTicks(yMax) : undefined}
              interval={0}
              tickFormatter={(value: number) =>
                value >= 1000 ? `${Number((value / 1000).toFixed(2))}k` : value.toString()
              }
            />
            <Tooltip cursor={{ fill: 'rgba(12,28,44,0.04)' }} content={<EnergyTooltip />} />
            {shown.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                stackId="energy"
                fill={s.color === DASH.shared ? DASH.sharedLight : DASH.networkLight}
                stroke={s.color}
                strokeWidth={1.5}
                name={s.label}
                maxBarSize={44}
                radius={i === shown.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderSeriesLegend = (series: { key: string; label: string; color: string }[], interactive: boolean) => (
    <div className="flex flex-wrap items-center gap-2">
      {series.map((s, i) => {
        const active = !interactive || visibleSeries.has(s.key);
        const pairKey = series[1 - i]?.key;
        const content = (
          <>
            <span className="w-3 h-3 rounded-[3px] flex-shrink-0" style={{ backgroundColor: active ? (s.color === DASH.shared ? DASH.sharedLight : DASH.networkLight) : 'transparent', border: `1.5px solid ${s.color}` }} />
            {s.label}
          </>
        );
        return interactive ? (
          <button
            key={s.key}
            type="button"
            onClick={() => toggleSeries(s.key, pairKey)}
            aria-pressed={active}
            className={`inline-flex items-center gap-2 h-8 pl-2.5 pr-3 rounded-full border text-[13px] font-medium transition-colors ${
              active ? 'border-[#0C1C2C] text-[#0C1C2C]' : 'border-[#C9DFE5] text-[#51626E] hover:border-[#00516F]'
            }`}
          >
            {content}
          </button>
        ) : (
          <span key={s.key} className="inline-flex items-center gap-2 text-sm text-[#0C1C2C] mr-3">{content}</span>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-white font-sans text-[#0C1C2C]">
      <MemberSidebar participant={participant} user={user} onRefresh={handleRefresh} />

      {/* Header */}
      <header className="bg-white border-b border-[#C9DFE5] flex items-center">
        {/* Logo dans le coin gauche, hors de la colonne centrale */}
        <img src="/images/Logosite2.png" alt="Sun Is Up Logo" className="h-20 w-20 flex-shrink-0 ml-3 sm:ml-4 my-2 object-contain" />
        <div className="flex-1 min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4 min-w-0">
              <h1 className="font-sans text-2xl leading-tight font-bold text-[#00516F] truncate">
                {dashboardTitle}
              </h1>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => { setTutorialMode('full'); setShowTutorial(true); }} className={DASH_BTN}>
                <HelpCircle className="w-4 h-4" />
                {t('memberUi.help')}
              </button>

              {isAdminView && (
                <button onClick={onLogout} className={DASH_BTN}>
                  <ArrowLeft className="w-4 h-4" />
                  {t('memberDashboard.backAdmin')}
                </button>
              )}

              {onSwitchService && availableServices.length > 1 && (
                <button onClick={onSwitchService} className={`${DASH_BTN} !border-[#FCAE0A] bg-[#FFF8E6] hover:bg-[#FFF1CC]`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  {t('memberUi.switchService')}
                </button>
              )}

              <button onClick={handleRefresh} disabled={refreshing} className={DASH_BTN}>
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? t('memberDashboard.refreshing') : t('memberDashboard.refresh')}
              </button>

              <button onClick={handleLogout} disabled={isLoggingOut} className={`${DASH_BTN} !border-[#0C1C2C]`}>
                <LogOut className="w-4 h-4" />
                {isLoggingOut ? t('memberDashboard.loggingOut') : t('memberDashboard.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-14 ml-14 flex flex-col gap-6">
        {/* Onglets + année */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#C9DFE5]">
          <div className="flex" role="tablist">
            <button role="tab" aria-selected={activeTab === 'energie'} onClick={() => setActiveTab('energie')} className={dashTabClass(activeTab === 'energie')}>
              {t('memberDashboard.tabs.energy')}
            </button>
            <button role="tab" data-tutorial="tutorial-contract-tab" aria-selected={activeTab === 'contrat'} onClick={() => setActiveTab('contrat')} className={dashTabClass(activeTab === 'contrat')}>
              {t('memberDashboard.tabs.contract')}
            </button>
          </div>
          {activeTab === 'energie' && (
          <label className="flex items-center gap-2 text-sm text-[#51626E] pb-1.5">
            <Calendar className="w-4 h-4" />
            {t('memberDashboard.year')}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className={DASH_SELECT}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </label>
          )}
        </div>

        {/* Bilan de l'année */}
        {activeTab === 'energie' && (
        <section
          aria-label={`${t('memberDashboard.stats.summary')} ${selectedYear}`}
          className="bg-white border border-[#C9DFE5] rounded-[20px] px-6 py-6 md:px-8 md:py-7 flex flex-wrap gap-8 items-stretch"
        >
          <div className="flex-[2_1_480px] min-w-0 flex flex-col gap-5">
            <span className="font-sans text-xs font-semibold uppercase tracking-wider text-[#00516F]">
              {t('memberDashboard.stats.summary')} {selectedYear}
            </span>
            {showBothBlocks ? (
              <>
                <BilanBlock
                  title={t('memberDashboard.chart.consumption')}
                  shared={yearlyTotals.volume_partage}
                  network={yearlyTotals.volume_complementaire}
                  sharedLabel={t('memberDashboard.stats.sharedConsumption')}
                  networkLabel={t('memberDashboard.stats.networkConsumption')}
                />
                <div className="h-px bg-[#C9DFE5]" />
                <BilanBlock
                  title={t('memberDashboard.chart.injection')}
                  shared={yearlyTotals.injection_partagee}
                  network={yearlyTotals.injection_complementaire}
                  sharedLabel={t('memberDashboard.stats.sharedInjection')}
                  networkLabel={t('memberDashboard.stats.networkInjection')}
                />
              </>
            ) : isConsumerGroup ? (
              <BilanBlock
                title={t('memberDashboard.chart.consumption')}
                shared={yearlyTotals.volume_partage}
                network={yearlyTotals.volume_complementaire}
                sharedLabel={t('memberDashboard.stats.sharedConsumption')}
                networkLabel={t('memberDashboard.stats.networkConsumption')}
                percentLabel={t('memberDashboard.stats.sharedPercentage')}
                percent={consumptionSharedPercentage}
              />
            ) : (
              <BilanBlock
                title={t('memberDashboard.chart.injection')}
                shared={yearlyTotals.injection_partagee}
                network={yearlyTotals.injection_complementaire}
                sharedLabel={t('memberDashboard.stats.sharedInjection')}
                networkLabel={t('memberDashboard.stats.networkInjection')}
                percentLabel={t('memberDashboard.stats.sharedInjectionPercentage')}
                percent={injectionSharedPercentage}
              />
            )}
          </div>
          <div className="flex-[1_1_220px] flex flex-col justify-center gap-4 md:border-l md:border-[#C9DFE5] md:pl-8">
            <BilanSide icon={<Leaf className="w-5 h-5" />} label={t('memberDashboard.stats.co2Avoided')} value={co2Label} />
            <div className="h-px bg-[#C9DFE5]" />
            <BilanSide
              icon={isGroupDashboard ? <Users className="w-5 h-5" /> : <User className="w-5 h-5" />}
              label={statusLabel}
              value={statusValue}
            />
          </div>
        </section>
        )}

        {activeTab === 'contrat' && (
          <ContractTab
            participant={participant}
            groupParticipants={groupParticipants}
            isGroupDashboard={!!isGroupDashboard}
            isMixedGroup={isMixedGroup}
            userEmail={user?.email ?? null}
            onUpdated={handleRefresh}
          />
        )}

        {activeTab === 'energie' && (
        <>

        {/* Graphique */}
        <section data-tutorial="tutorial-monthly-chart" className={DASH_CARD}>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
            <div>
              <h2 className={DASH_H2}>
                {isGroupDashboard ? `${t('memberDashboard.chart.groupEvolution')} ${participant.groupe}` : t('memberDashboard.chart.yourEvolution')}
              </h2>
              <p className="mt-1 text-sm text-[#51626E]">
                {t('memberDashboard.chart.monthlyData')} {selectedYear} · kWh
              </p>
            </div>
            {isGroupDashboard && groupParticipants.length > 1 && (
              <label className="flex items-center gap-2 text-sm text-[#51626E]">
                <Users className="w-4 h-4" />
                <select
                  value={energySelectedParticipantId}
                  onChange={(e) => setEnergySelectedParticipantId(e.target.value)}
                  className={DASH_SELECT}
                >
                  <option value="all">{t('memberUi.allParticipants')}</option>
                  {groupParticipants.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {energyChartData.length > 0 ? (
            isMultiChart ? (
              <div className="flex flex-col gap-7">
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <h3 className="font-sans text-sm font-bold uppercase tracking-wide text-[#0C1C2C]">
                      {t('memberDashboard.chart.consumption')}
                    </h3>
                    {renderSeriesLegend(consoSeries, true)}
                  </div>
                  {renderEnergyChart(consoSeries, 'h-64', consoYMax)}
                </div>
                <div className="border-t border-[#E3ECEF] pt-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <h3 className="font-sans text-sm font-bold uppercase tracking-wide text-[#0C1C2C]">
                      {t('memberDashboard.chart.injection')}
                    </h3>
                    {renderSeriesLegend(injSeries, true)}
                  </div>
                  {renderEnergyChart(injSeries, 'h-64', injYMax)}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex flex-wrap items-center justify-end gap-3 mb-2">
                  {renderSeriesLegend(energyChartType === 'consumer' ? consoSeries : injSeries, false)}
                </div>
                {renderEnergyChart(energyChartType === 'consumer' ? consoSeries : injSeries, 'h-80',
                  Math.max(0, ...energyChartData.map((d: any) => (energyChartType === 'consumer' ? consoSeries : injSeries).reduce((sum, x) => sum + (Number(d[x.key]) || 0), 0))))}
              </div>
            )
          ) : (
            <div className="text-center text-[#51626E] py-12">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-[#C9DFE5]" />
              <p className="text-lg font-medium mb-2 text-[#0C1C2C]">{t('memberUi.noData')}</p>
              <p className="text-sm">{t('memberUi.noDataYear', { year: selectedYear })}</p>
              {availableDataPeriod && (
                <div className="mt-4 p-4 bg-[#EEF5F7] border border-[#C9DFE5] rounded-xl inline-block">
                  <p className="text-sm font-medium text-[#00516F] mb-1">{t('memberUi.availablePeriod')}</p>
                  <p className="text-sm text-[#0C1C2C]">
                    {format(new Date(availableDataPeriod.start + '-01'), 'MMMM yyyy', { locale: getDateLocale() })} - {format(new Date(availableDataPeriod.end + '-01'), 'MMMM yyyy', { locale: getDateLocale() })}
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Graphique détaillé des données CSV */}
        {participant?.ean_code && participant?.show_qh_data && canViewQh && (
          <>
            {!showQhChart ? (
              <section data-tutorial="tutorial-qh-data" className={`${DASH_CARD} flex flex-wrap gap-7 items-center`}>
                <div className="flex-[1_1_320px] flex flex-col gap-3.5 items-start">
                  <h2 className={DASH_H2}>{t('memberDashboard.qhData.title')}</h2>
                  <p className="text-[15px] leading-relaxed text-[#51626E]">
                    {t('memberDashboard.qhData.description')}
                  </p>
                  <button
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      const defaultStart = availableDataPeriod?.start
                        ? new Date(availableDataPeriod.start + '-01').toISOString().split('T')[0]
                        : today;
                      const defaultEnd = availableDataPeriod?.end
                        ? new Date(availableDataPeriod.end + '-01').toISOString().split('T')[0]
                        : today;
                      setQhModalStart(defaultStart);
                      setQhModalEnd(defaultEnd);
                      setShowQhPeriodModal(true);
                    }}
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-[10px] font-semibold text-[15px] text-[#0C1C2C] bg-[#FCAE0A] hover:bg-[#F0A200] transition-colors"
                  >
                    <BarChart3 className="w-4 h-4" />
                    {t('memberDashboard.qhData.showButton')}
                  </button>
                </div>
                <div className="flex-[2_1_380px] min-w-0 bg-[#F2F2F3] rounded-xl px-2 pt-4 pb-2" aria-hidden="true">
                  <svg viewBox="0 0 1040 120" width="100%" preserveAspectRatio="none" className="block h-24">
                    <path d={QH_PREVIEW.total} fill="#D6E4EA" />
                    <path d={QH_PREVIEW.totalLine} fill="none" stroke={DASH.network} strokeWidth={1.5} />
                    <path d={QH_PREVIEW.shared} fill="#FDE6CB" />
                    <path d={QH_PREVIEW.sharedLine} fill="none" stroke={DASH.shared} strokeWidth={1.5} />
                  </svg>
                </div>
              </section>
            ) : (
              <MemberEanChart
                participantEan={participant.ean_code}
                participantType={participant.type}
                groupEans={groupParticipants.map(p => p.ean_code).filter(Boolean)}
                autoRefresh={true}
                periodStart={qhPeriodStart}
                periodEnd={qhPeriodEnd}
              />
            )}

            {/* Modal de sélection de période */}
            {showQhPeriodModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowQhPeriodModal(false)}>
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-[#FFF8E6] border border-[#FCD98A] flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-[#00516F]" />
                    </div>
                    <div>
                      <h3 className="font-sans text-lg font-bold text-[#00516F]">{t('memberUi.qhPeriodTitle')}</h3>
                      <p className="text-sm text-neutral-500">{t('memberUi.qhPeriodText')}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-neutral-500 mb-1.5">{t('memberUi.startDate')}</label>
                      <input
                        type="date"
                        value={qhModalStart}
                        onChange={e => {
                          setQhModalStart(e.target.value);
                          if (e.target.value > qhModalEnd) setQhModalEnd(e.target.value);
                        }}
                        className="w-full px-3 py-2.5 text-sm border border-[#C9DFE5] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#00516F]/30"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-neutral-500 mb-1.5">{t('memberUi.endDate')}</label>
                      <input
                        type="date"
                        value={qhModalEnd}
                        onChange={e => setQhModalEnd(e.target.value)}
                        min={qhModalStart}
                        className="w-full px-3 py-2.5 text-sm border border-[#C9DFE5] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#00516F]/30"
                      />
                    </div>
                    {availableDataPeriod && (
                      <div className="text-xs text-neutral-400 bg-neutral-50 rounded-lg p-3">
                        {t('memberUi.availablePeriod')} {format(new Date(availableDataPeriod.start + '-01'), 'MMM yyyy', { locale: getDateLocale() })} — {format(new Date(availableDataPeriod.end + '-01'), 'MMM yyyy', { locale: getDateLocale() })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-3 mt-6">
                    <button
                      onClick={() => setShowQhPeriodModal(false)}
                      className="px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                    >
                      {t('memberUi.cancel')}
                    </button>
                    <button
                      onClick={() => {
                        setQhPeriodStart(qhModalStart);
                        setQhPeriodEnd(qhModalEnd);
                        setShowQhPeriodModal(false);
                        setShowQhChart(true);
                      }}
                      disabled={!qhModalStart || !qhModalEnd || qhModalStart > qhModalEnd}
                      className="px-5 py-2 text-sm font-semibold text-[#0C1C2C] bg-[#FCAE0A] hover:bg-[#F0A200] disabled:bg-neutral-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      {t('memberUi.showChart')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tableau détaillé des participants du groupe */}
        {isGroupDashboard && (
          <section data-tutorial="tutorial-group-table" className={DASH_CARD}>
            <div className="flex items-end justify-between mb-4 flex-wrap gap-4">
              <div>
                <h2 className={DASH_H2}>
                  {t('memberDashboard.table.title')} {participant.groupe}
                </h2>
                <p className="mt-1 text-sm text-[#51626E]">{detailViewMode === 'economique' ? '€ HTVA' : energyUnit}</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center">
                  <label className="block text-xs font-medium text-[#51626E] mr-2">{t('memberUi.unit')}</label>
                  <div className="inline-flex rounded-[10px] border border-[#C9DFE5] overflow-hidden">
                    <button
                      onClick={() => setEnergyUnit('kWh')}
                      className={DASH_SEG(energyUnit === 'kWh')}
                    >
                      kWh
                    </button>
                    <button
                      onClick={() => setEnergyUnit('MWh')}
                      className={DASH_SEG(energyUnit === 'MWh')}
                    >
                      MWh
                    </button>
                  </div>
                </div>
                <div className="flex items-center">
                  <label className="block text-xs font-medium text-[#51626E] mr-2">{t('memberUi.view')}</label>
                  <div className="inline-flex rounded-[10px] border border-[#C9DFE5] overflow-hidden">
                    <button
                      onClick={() => setDetailViewMode('energetique')}
                      className={DASH_SEG(detailViewMode === 'energetique')}
                    >
                      {t('memberUi.energyView')}
                    </button>
                    <button
                      onClick={() => setDetailViewMode('economique')}
                      className={DASH_SEG(detailViewMode === 'economique')}
                    >
                      {t('memberUi.economicView')}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#51626E] mb-1">{t('memberUi.start')}</label>
                  <select
                    value={energyDetailStart}
                    onChange={e => {
                      setEnergyDetailStart(e.target.value);
                      if (e.target.value > energyDetailEnd) setEnergyDetailEnd(e.target.value);
                    }}
                    className={DASH_SELECT}
                  >
                    {energyDetailMonthsWithData.map(k => {
                      const [y, m] = k.split('-');
                      const date = new Date(Number(y), Number(m) - 1, 1);
                      return <option key={k} value={k}>{format(date, 'MMM yyyy', { locale: getDateLocale() })}</option>;
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#51626E] mb-1">{t('memberUi.end')}</label>
                  <select
                    value={energyDetailEnd}
                    onChange={e => setEnergyDetailEnd(e.target.value)}
                    className={DASH_SELECT}
                  >
                    {energyDetailMonthsWithData.filter(k => k >= energyDetailStart).map(k => {
                      const [y, m] = k.split('-');
                      const date = new Date(Number(y), Number(m) - 1, 1);
                      return <option key={k} value={k}>{format(date, 'MMM yyyy', { locale: getDateLocale() })}</option>;
                    })}
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto -mx-1">
              {(() => {
                const keysToUse = energyDetailMonthsWithData.filter(k =>
                  (!energyDetailStart || k >= energyDetailStart) && (!energyDetailEnd || k <= energyDetailEnd)
                );
                const sortedGroupParticipants = [...groupParticipants].sort((a, b) => {
                  const nameA = ((a as any).company_name || '').toLowerCase();
                  const nameB = ((b as any).company_name || '').toLowerCase();
                  if (nameA && nameB) return nameA.localeCompare(nameB);
                  if (nameA) return -1;
                  if (nameB) return 1;
                  return (a.name || '').localeCompare(b.name || '');
                });

                if (detailViewMode === 'economique') {
                  const fmtEUR = (v: number) => v.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
                  const DEFAULT_BILLING: Record<string, number> = {
                    tva_entreprise: 0.21, tva_particulier: 0.06, cotisation_energie: 1.9261,
                    accise_ent_t1: 14.21, accise_ent_t2: 12.09, accise_ent_t3: 11.39, accise_ent_t4: 10.69,
                    accise_part_t1: 47.48, accise_part_t2: 45.46, accise_part_t3: 44.78, accise_part_t4: 44.11,
                    adhesion_tvac: 60.50,
                  };
                  const bc = { ...DEFAULT_BILLING, ...(detailBillingConfig || {}) };

                  const resolvePrice = (basePrice: any, overrides: any[], priceType: string, monthKey: string) => {
                    const filtered = overrides.filter((o: any) => o.price_type === priceType);
                    return resolvePriceForMonth(basePrice != null ? Number(basePrice) : null, filtered as TariffOverride[], monthKey);
                  };

                  const computeEcoForParticipant = (p: any) => {
                    let monthlyData: any = {};
                    try { monthlyData = typeof p.monthly_data === 'string' ? JSON.parse(p.monthly_data) : (p.monthly_data || {}); } catch {}
                    let billingData: any = {};
                    try { billingData = typeof p.billing_data === 'string' ? JSON.parse(p.billing_data) : (p.billing_data || {}); } catch {}

                    const annualConsumption = Number(p.annual_consumption || 30);
                    const isCompany = !!p.company_number;
                    const overrides = detailTariffOverrides[p.id] || [];

                    let totalReseau = 0, totalAccises = 0, totalCommunity = 0, totalInjection = 0;

                    keysToUse.forEach(monthKey => {
                      const md = monthlyData[monthKey];
                      if (!md) return;
                      const volPartage = Number(md.volume_partage || 0);
                      const sharedMWh = volPartage / 1000;
                      const injectionPartagee = Number(md.injection_partagee || 0);
                      const injectionMWh = injectionPartagee / 1000;

                      // Shared energy buy price (prosumer buy price takes priority, matching invoice logic)
                      let sharedPrice = 0;
                      if (p.type === 'prosumer' && p.prosumer_buy_price != null) {
                        sharedPrice = resolvePrice(p.prosumer_buy_price, overrides, 'prosumer_buy_price', monthKey);
                      } else if (p.shared_energy_formula_type === 'variable' && p.shared_energy_a && p.shared_energy_x_param) {
                        const a = parseFloat(p.shared_energy_a) || 0;
                        const paramValues = detailIdxValues[p.shared_energy_x_param] || {};
                        const xVal = paramValues[monthKey];
                        sharedPrice = xVal !== undefined ? a * xVal : Number(p.shared_energy_price || 100);
                      } else {
                        sharedPrice = resolvePrice(p.shared_energy_price, overrides, 'shared_energy_price', monthKey);
                      }
                      totalCommunity += sharedMWh * sharedPrice;

                      // Sell price for injection
                      let sellPrice = 0;
                      if (p.type === 'prosumer' && p.prosumer_sell_price != null) {
                        sellPrice = resolvePrice(p.prosumer_sell_price, overrides, 'prosumer_sell_price', monthKey);
                      } else if (p.shared_energy_formula_type === 'variable' && p.shared_energy_a && p.shared_energy_x_param) {
                        const a = parseFloat(p.shared_energy_a) || 0;
                        const paramValues = detailIdxValues[p.shared_energy_x_param] || {};
                        const xVal = paramValues[monthKey];
                        sellPrice = xVal !== undefined ? a * xVal : Number(p.shared_energy_price || 100);
                      } else {
                        sellPrice = resolvePrice(p.shared_energy_price, overrides, 'shared_energy_price', monthKey);
                      }
                      totalInjection += injectionMWh * sellPrice;

                      // Network costs (raw, no multiplier)
                      totalReseau += Number(billingData[monthKey]?.networkCosts?.totalFraisReseau || 0);

                      // Accises
                      let droitAcciseRate = 0;
                      const cotEnergie = bc.cotisation_energie;
                      if (isCompany) {
                        if (annualConsumption <= 20) droitAcciseRate = bc.accise_ent_t1;
                        else if (annualConsumption <= 50) droitAcciseRate = bc.accise_ent_t2;
                        else if (annualConsumption <= 1000) droitAcciseRate = bc.accise_ent_t3;
                        else droitAcciseRate = bc.accise_ent_t4;
                      } else {
                        if (annualConsumption <= 20) droitAcciseRate = bc.accise_part_t1;
                        else if (annualConsumption <= 50) droitAcciseRate = bc.accise_part_t2;
                        else if (annualConsumption <= 1000) droitAcciseRate = bc.accise_part_t3;
                        else droitAcciseRate = bc.accise_part_t4;
                      }
                      totalAccises += sharedMWh * (droitAcciseRate + cotEnergie);
                    });

                    const net = totalReseau + totalAccises + totalCommunity - totalInjection;
                    return { totalReseau, totalAccises, totalCommunity, totalInjection, net };
                  };

                  const ecoData = sortedGroupParticipants.map(p => ({ participant: p, eco: computeEcoForParticipant(p) }));

                  return (
                    <table className="w-full text-[13.5px]">
                      <thead>
                        <tr>
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-left">{t('memberUi.colParticipant')}</th>
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-left">{t('memberUi.colCompany')}</th>
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-left">{t('memberUi.colType')}</th>
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right whitespace-nowrap"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: DASH.network }} />{t('memberUi.colNetwork')}</span></th>
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right whitespace-nowrap"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: DASH.excise }} />{t('memberUi.colExcise')}</span></th>
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right whitespace-nowrap"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: DASH.shared }} />{t('memberUi.colCommunity')}</span></th>
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right whitespace-nowrap"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: DASH.shared }} />{t('memberUi.colInjection')}</span></th>
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right whitespace-nowrap">{t('memberUi.colNet')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ecoData.map(({ participant: p, eco }) => (
                          <tr key={p.id} className="border-b border-[#E3ECEF] hover:bg-[#F7FAFB]">
                            <td className="px-2 py-3 whitespace-nowrap">
                              <div className="font-semibold text-[#0C1C2C] flex items-center gap-1.5">{p.name}</div>
                              {p.ean_code && <div className="text-[11px] text-[#51626E] font-mono mt-0.5">{p.ean_code}</div>}
                            </td>
                            <td className="px-2 py-3 max-w-[180px]">
                              {(p as any).company_name ? (
                                <span className="text-[#0C1C2C]">{(p as any).company_name}</span>
                              ) : (
                                <span className="text-[#A7B3BC]">—</span>
                              )}
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap">
                              <TypeChip type={p.type} label={p.type === 'producer' ? t('memberDashboard.table.producer') : p.type === 'prosumer' ? 'Prosumer' : t('memberDashboard.table.consumer')} />
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap text-right text-[#0C1C2C] tabular-nums">{fmtEUR(eco.totalReseau)}</td>
                            <td className="px-2 py-3 whitespace-nowrap text-right text-[#0C1C2C] tabular-nums">{fmtEUR(eco.totalAccises)}</td>
                            <td className="px-2 py-3 whitespace-nowrap text-right text-[#0C1C2C] tabular-nums">{fmtEUR(eco.totalCommunity)}</td>
                            <td className="px-2 py-3 whitespace-nowrap text-right text-[#B35F0F] tabular-nums">{eco.totalInjection > 0 ? '-' : ''}{fmtEUR(eco.totalInjection)}</td>
                            <td className={`px-2 py-3 whitespace-nowrap text-right font-semibold tabular-nums ${eco.net < 0 ? 'text-[#B35F0F]' : 'text-[#0C1C2C]'}`}>{fmtEUR(eco.net)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-[#F7FAFB]">
                        <tr>
                          <td colSpan={3} className="px-2 py-3 font-semibold text-[#0C1C2C]">{t('memberDashboard.table.totals')}</td>
                          <td className="px-2 py-3 whitespace-nowrap text-right font-semibold text-[#0C1C2C] tabular-nums">{fmtEUR(ecoData.reduce((s, d) => s + d.eco.totalReseau, 0))}</td>
                          <td className="px-2 py-3 whitespace-nowrap text-right font-semibold text-[#0C1C2C] tabular-nums">{fmtEUR(ecoData.reduce((s, d) => s + d.eco.totalAccises, 0))}</td>
                          <td className="px-2 py-3 whitespace-nowrap text-right font-semibold text-[#0C1C2C] tabular-nums">{fmtEUR(ecoData.reduce((s, d) => s + d.eco.totalCommunity, 0))}</td>
                          <td className="px-2 py-3 whitespace-nowrap text-right font-semibold text-[#B35F0F] tabular-nums">{(() => { const tot = ecoData.reduce((s, d) => s + d.eco.totalInjection, 0); return tot > 0 ? '-' : ''; })()}{fmtEUR(ecoData.reduce((s, d) => s + d.eco.totalInjection, 0))}</td>
                          <td className={`px-2 py-3 whitespace-nowrap text-right font-semibold tabular-nums ${ecoData.reduce((s, d) => s + d.eco.net, 0) < 0 ? 'text-[#B35F0F]' : 'text-[#0C1C2C]'}`}>{fmtEUR(ecoData.reduce((s, d) => s + d.eco.net, 0))}</td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                }

                // Énergétique view (existing logic)
                const allParticipantsData = sortedGroupParticipants.map((groupParticipant) => {
                  const allData = processIndividualMonthlyDataAllYears(groupParticipant);
                  const displayData = allData
                    .filter(d => keysToUse.includes(d.monthKey))
                    .reduce((totals, month) => ({
                      volume_partage: totals.volume_partage + month.volume_partage,
                      volume_complementaire: totals.volume_complementaire + month.volume_complementaire,
                      injection_partagee: totals.injection_partagee + month.injection_partagee,
                      injection_complementaire: totals.injection_complementaire + month.injection_complementaire
                    }), {
                      volume_partage: 0,
                      volume_complementaire: 0,
                      injection_partagee: 0,
                      injection_complementaire: 0
                    });
                  return { participant: groupParticipant, data: displayData };
                });

                const hasProducer = allParticipantsData.some(({ participant: p }) => p.type === 'producer' || p.type === 'prosumer');
                const hasConsumer = allParticipantsData.some(({ participant: p }) => p.type === 'consumer' || p.type === 'prosumer');
                const showInjCols = hasProducer;
                const showConsoCols = hasConsumer;

                return (
                  <table className="w-full text-[13.5px]">
                    <thead>
                      <tr>
                        <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-left">
                          {t('memberDashboard.table.participant')}
                        </th>
                        <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-left">
                          {t('memberDashboard.table.companyName')}
                        </th>
                        <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-left">
                          {t('memberDashboard.table.type')}
                        </th>
                        {showInjCols && (
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right">
                            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: DASH.shared }} />{t('memberDashboard.table.sharedInjection')}</span>
                          </th>
                        )}
                        {showInjCols && (
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right">
                            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: DASH.network }} />{t('memberDashboard.table.networkInjection')}</span>
                          </th>
                        )}
                        {showConsoCols && (
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right">
                            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: DASH.shared }} />{t('memberDashboard.table.sharedConsumption')}</span>
                          </th>
                        )}
                        {showConsoCols && (
                          <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-right">
                            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: DASH.network }} />{t('memberDashboard.table.networkConsumption')}</span>
                          </th>
                        )}
                        <th className="px-2 py-2.5 border-b border-[#C9DFE5] align-bottom font-sans text-[11px] font-semibold uppercase tracking-wide text-[#51626E] text-left">
                          {t('memberDashboard.table.sharePercent')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {allParticipantsData.map(({ participant: groupParticipant, data: displayData }) => {
                        const isProducer = groupParticipant.type === 'producer';
                        const isProsumerParticipant = groupParticipant.type === 'prosumer';
                        const isConsumer = groupParticipant.type === 'consumer';

                        const totalInjection = displayData.injection_partagee + displayData.injection_complementaire;
                        const injectionPercentage = totalInjection > 0
                          ? (displayData.injection_partagee / totalInjection) * 100
                          : 0;
                        const totalConso = displayData.volume_partage + displayData.volume_complementaire;
                        const consoPercentage = totalConso > 0
                          ? (displayData.volume_partage / totalConso) * 100
                          : 0;

                        const sharePercent = isProducer
                          ? injectionPercentage
                          : isProsumerParticipant
                          ? (() => {
                              const total = totalInjection + totalConso;
                              const shared = displayData.injection_partagee + displayData.volume_partage;
                              return total > 0 ? (shared / total) * 100 : 0;
                            })()
                          : consoPercentage;

                        return (
                          <tr key={groupParticipant.id} className="border-b border-[#E3ECEF] hover:bg-[#F7FAFB]">
                            <td className="px-2 py-3 whitespace-nowrap">
                              <div className="font-semibold text-[#0C1C2C] flex items-center gap-1.5">
                                {groupParticipant.name}
                                </div>
                              {groupParticipant.ean_code && (
                                <div className="text-[11px] text-[#51626E] font-mono mt-0.5">
                                  {groupParticipant.ean_code}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-3 max-w-[180px]">
                              {(groupParticipant as any).company_name ? (
                                <span className="text-[#0C1C2C]">{(groupParticipant as any).company_name}</span>
                              ) : (
                                <span className="text-[#A7B3BC]">—</span>
                              )}
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap">
                              <TypeChip type={groupParticipant.type} label={isProducer ? t('memberDashboard.table.producer') : isProsumerParticipant ? 'Prosumer' : t('memberDashboard.table.consumer')} />
                            </td>
                            {showInjCols && (
                              <td className="px-2 py-3 whitespace-nowrap text-right tabular-nums text-[#0C1C2C]">
                                {(isProducer || isProsumerParticipant)
                                  ? `${(displayData.injection_partagee / (energyUnit === 'MWh' ? 1000 : 1)).toFixed(2)} ${energyUnit}`
                                  : <span className="text-[#A7B3BC]">—</span>
                                }
                              </td>
                            )}
                            {showInjCols && (
                              <td className="px-2 py-3 whitespace-nowrap text-right tabular-nums text-[#0C1C2C]">
                                {(isProducer || isProsumerParticipant)
                                  ? `${(displayData.injection_complementaire / (energyUnit === 'MWh' ? 1000 : 1)).toFixed(2)} ${energyUnit}`
                                  : <span className="text-[#A7B3BC]">—</span>
                                }
                              </td>
                            )}
                            {showConsoCols && (
                              <td className="px-2 py-3 whitespace-nowrap text-right tabular-nums text-[#0C1C2C]">
                                {(isConsumer || isProsumerParticipant)
                                  ? `${(displayData.volume_partage / (energyUnit === 'MWh' ? 1000 : 1)).toFixed(2)} ${energyUnit}`
                                  : <span className="text-[#A7B3BC]">—</span>
                                }
                              </td>
                            )}
                            {showConsoCols && (
                              <td className="px-2 py-3 whitespace-nowrap text-right tabular-nums text-[#0C1C2C]">
                                {(isConsumer || isProsumerParticipant)
                                  ? `${(displayData.volume_complementaire / (energyUnit === 'MWh' ? 1000 : 1)).toFixed(2)} ${energyUnit}`
                                  : <span className="text-[#A7B3BC]">—</span>
                                }
                              </td>
                            )}
                            <td className="px-2 py-3 whitespace-nowrap tabular-nums text-[#0C1C2C]">
                              <span className="inline-block align-middle w-14 h-2 rounded bg-[#E3ECEF] overflow-hidden mr-2.5">
                                <span className="block h-2" style={{ width: `${Math.min(100, Math.max(0, sharePercent))}%`, background: DASH.shared }} />
                              </span>
                              {`${sharePercent.toFixed(1)}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-[#F7FAFB]">
                      <tr>
                        <td colSpan={3} className="px-2 py-3 font-semibold text-[#0C1C2C]">
                          {t('memberDashboard.table.totals')}
                        </td>
                        {showInjCols && (
                          <td className="px-2 py-3 whitespace-nowrap text-right tabular-nums font-semibold text-[#0C1C2C]">
                            {(() => {
                              const total = allParticipantsData.reduce((sum, { participant: p, data }) => {
                                if (p.type === 'producer' || p.type === 'prosumer') return sum + data.injection_partagee;
                                return sum;
                              }, 0);
                              return `${(total / (energyUnit === 'MWh' ? 1000 : 1)).toFixed(2)} ${energyUnit}`;
                            })()}
                          </td>
                        )}
                        {showInjCols && (
                          <td className="px-2 py-3 whitespace-nowrap text-right tabular-nums font-semibold text-[#0C1C2C]">
                            {(() => {
                              const total = allParticipantsData.reduce((sum, { participant: p, data }) => {
                                if (p.type === 'producer' || p.type === 'prosumer') return sum + data.injection_complementaire;
                                return sum;
                              }, 0);
                              return `${(total / (energyUnit === 'MWh' ? 1000 : 1)).toFixed(2)} ${energyUnit}`;
                            })()}
                          </td>
                        )}
                        {showConsoCols && (
                          <td className="px-2 py-3 whitespace-nowrap text-right tabular-nums font-semibold text-[#0C1C2C]">
                            {(() => {
                              const total = allParticipantsData.reduce((sum, { participant: p, data }) => {
                                if (p.type === 'consumer' || p.type === 'prosumer') return sum + data.volume_partage;
                                return sum;
                              }, 0);
                              return `${(total / (energyUnit === 'MWh' ? 1000 : 1)).toFixed(2)} ${energyUnit}`;
                            })()}
                          </td>
                        )}
                        {showConsoCols && (
                          <td className="px-2 py-3 whitespace-nowrap text-right tabular-nums font-semibold text-[#0C1C2C]">
                            {(() => {
                              const total = allParticipantsData.reduce((sum, { participant: p, data }) => {
                                if (p.type === 'consumer' || p.type === 'prosumer') return sum + data.volume_complementaire;
                                return sum;
                              }, 0);
                              return `${(total / (energyUnit === 'MWh' ? 1000 : 1)).toFixed(2)} ${energyUnit}`;
                            })()}
                          </td>
                        )}
                        <td className="px-2 py-3 whitespace-nowrap tabular-nums font-semibold text-[#0C1C2C]">
                          {(() => {
                            let totalShared = 0;
                            let totalAll = 0;
                            allParticipantsData.forEach(({ participant: p, data }) => {
                              if (p.type === 'producer') {
                                totalShared += data.injection_partagee;
                                totalAll += data.injection_partagee + data.injection_complementaire;
                              } else if (p.type === 'prosumer') {
                                totalShared += data.volume_partage + data.injection_partagee;
                                totalAll += data.volume_partage + data.volume_complementaire + data.injection_partagee + data.injection_complementaire;
                              } else {
                                totalShared += data.volume_partage;
                                totalAll += data.volume_partage + data.volume_complementaire;
                              }
                            });
                            return totalAll > 0 ? `${((totalShared / totalAll) * 100).toFixed(1)}%` : '—';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                );
              })()}
            </div>
          </section>
        )}
        </>
        )}
      </main>

      <MemberTutorial isOpen={showTutorial} onClose={handleCloseTutorial} mode={tutorialMode} onTabChange={setActiveTab}
        hiddenTargets={(isGroupDashboard ? groupParticipants : [participant]).some(p => p && p.type !== 'producer') ? [] : ['tutorial-contract-offer']} />
    </div>
  );
}